import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '@/auth/useAuth';
import { DropdownMenu, type DropdownMenuItem } from '@/components/ui';

/**
 * EAA-1804: turns the appbar identity chip into a dropdown so logout is
 * reachable from anywhere, not just `/profile`. Today logout only exists on
 * `ProfilePage` ("Đăng xuất khỏi tất cả"), so a user working inside `/console`
 * has to navigate away first to sign out. `AppBar` renders this in place of
 * the old `<Link to="/profile">` chip; the trigger keeps the same
 * `.portal-appbar-user` / `.portal-avatar` markup so existing appbar styling
 * is unaffected.
 *
 * EAA-1839: this used to hand-roll the popup itself — a `role="menu"` div with
 * `role="menuitem"` children, plus ~45 lines of outside-click/Escape
 * dismissal and roving ArrowUp/ArrowDown focus management. That is exactly
 * what the kit's `DropdownMenu` (packages/ui, backed by antd's
 * `Dropdown`+`Menu`) already owns: outside-click/Escape dismissal, and the
 * arrow-key/Home/End roving focus *within* an already-focused menu, so all of
 * that plumbing is deleted below in favour of a declarative `items` list.
 *
 * What is NOT free: antd v6's `Dropdown` (see `antd/es/dropdown/dropdown.js`)
 * only clones `className`/`disabled` onto a custom `children` trigger — it
 * has no `focus`/`autoFocus` handling at all, does not add
 * `aria-haspopup`/`aria-expanded` to that trigger, and does not move focus
 * into the menu on open or back to the trigger on close the way the old
 * hand-rolled version did. Verified live via chrome-devtools: with an
 * uncontrolled custom trigger, Enter/Space opened the menu but focus stayed
 * on the trigger button, so ArrowDown had nothing to move focus *from*. The
 * `open`/`onOpenChange` control plus the effect below restores that focus
 * handoff in both directions (the same shape the old code's effect used),
 * and the aria attributes are set explicitly since antd does not supply them.
 */
export function UserMenu() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { authenticated, claims, logout } = useAuth();
    const displayName = claims?.name ?? claims?.email ?? claims?.sub ?? '';
    const initial = displayName ? displayName.trim().charAt(0) : '?';

    const [open, setOpen] = useState(false);
    const [loggingOut, setLoggingOut] = useState(false);
    const triggerRef = useRef<HTMLButtonElement>(null);
    // Tracks the previous `open` value so the effect below can tell "just
    // closed" apart from "just mounted" — both see `open === false`, but only
    // the former should steal focus back onto the trigger.
    const wasOpenRef = useRef(false);

    // EAA-1839: antd's Dropdown doesn't move focus itself (see note above),
    // so this reinstates the old hand-rolled behaviour — focus the first
    // item once the overlay is in the DOM, and hand focus back to the
    // trigger once it closes. Scoped by `overlayClassName` below so this
    // never grabs a menu item belonging to some other open dropdown.
    //
    // The `requestAnimationFrame` is load-bearing, not decorative: verified
    // live (chrome-devtools) that the `[role="menuitem"]` element already
    // exists with the right class at the moment this effect's synchronous
    // body runs (`open` just flipped true), yet `.focus()` on it there is a
    // no-op — the antd/rc-trigger portal that mounts the overlay does so on
    // a slightly later tick than the state commit this effect reacts to, and
    // focusing it manually a beat later (see the same DOM query eval'd on
    // its own) works fine. One rAF is enough to land after that mount.
    useEffect(() => {
        if (open) {
            const raf = requestAnimationFrame(() => {
                document
                    .querySelector<HTMLElement>('.portal-user-menu-overlay [role="menuitem"]')
                    ?.focus();
            });
            wasOpenRef.current = open;
            return () => cancelAnimationFrame(raf);
        }
        if (wasOpenRef.current) {
            triggerRef.current?.focus();
        }
        wasOpenRef.current = open;
        return undefined;
    }, [open]);

    async function handleLogout() {
        if (loggingOut) return;
        setLoggingOut(true);
        try {
            await logout();
        } finally {
            setLoggingOut(false);
        }
    }

    if (!authenticated || !displayName) {
        return null;
    }

    const items: DropdownMenuItem[] = [
        {
            key: 'profile',
            label: t('shell.userMenu.profileTitle'),
            onSelect: () => navigate('/profile'),
        },
        {
            key: 'logout',
            label: loggingOut ? t('shell.userMenu.loggingOut') : t('shell.userMenu.logout'),
            danger: true,
            disabled: loggingOut,
            onSelect: () => void handleLogout(),
        },
    ];

    return (
        <DropdownMenu
            items={items}
            trigger={['click']}
            open={open}
            onOpenChange={setOpen}
            overlayClassName="portal-user-menu-overlay"
        >
            <button
                type="button"
                ref={triggerRef}
                className="portal-appbar-user"
                aria-label={t('shell.userMenu.profileTitle')}
                aria-haspopup="menu"
                aria-expanded={open}
                title={t('shell.userMenu.profileTitle')}
            >
                <span className="portal-avatar" aria-hidden="true">
                    {initial}
                </span>
                <span>{displayName}</span>
            </button>
        </DropdownMenu>
    );
}
